/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import * as H from 'history';

import React, { ChangeEvent } from 'react';
import { ViewModel } from '../../../core/viewmodel/ViewModel';
import { MaterialPopover } from '../../../models/material/MaterialPopover';
import { ProfileModel } from '../../../models/components/ProfileModel';
import { AuthStore } from '../../../stores/application/AuthStore';
import { ProfileService } from '../../../services/ProfileService';
import { SnackbarStore } from '../../../stores/application/SnackbarStore';
import { EventStore } from '../../../stores/application/EventStore';
import { StoreProvider } from '../../../stores/StoreProvider';
import { EventTypes } from '../../../models/application/EventModel';

export class ProfileViewModel extends ViewModel {
	public popover: MaterialPopover = new MaterialPopover();
	public model: ProfileModel = new ProfileModel();
	private readonly authStore: AuthStore;
	private readonly profileService: ProfileService;
	private readonly snackbarStore: SnackbarStore;
	private readonly eventStore: EventStore;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.authStore = storeProvider.authStore;
		this.profileService = storeProvider.profileService;
		this.snackbarStore = storeProvider.snackbarStore;
		this.eventStore = storeProvider.eventStore;
	}

	private readonly imagePlaceholder = '/img/profile.svg';

	public isOpen = (): boolean => {
		return this.popover.open;
	};

	public openPopover = (event: React.MouseEvent<Element, MouseEvent>): void => {
		this.popover.openPopover(event);
	};

	public closePopover = (): void => {
		this.popover.closePopover();
	};

	public getAnchorEl = (): Element | null => {
		return this.popover.anchor;
	};

	public getProfileImage = (): string => {
		const imgUrl = this.authStore.getImgUrl();
		return imgUrl === '' ? this.imagePlaceholder : imgUrl;
	};

	public getUsername = (): string => {
		return this.authStore.getUsername();
	};

	public logout = (): void => {
		this.authStore.removeAuthUser();
		this.eventStore.addEvent(EventTypes.USER_LOGOUT);
	};

	public setIsInput(bool: boolean): void {
		this.model.setIsInput(bool);
	}

	public changeName(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		this.model.setName(event.target.value);
	}

	public updateName(): void {
		if (this.model.name !== '') {
			const authToken: string = this.authStore.getAuthToken();

			this.profileService
				.updateName(this.model.name, authToken)
				.then(() => {
					this.snackbarStore.showSuccess('profile.name_updated');
					this.authStore.setUsername(this.model.name);
					this.eventStore.addEvent(EventTypes.USER_UPDATE);
				})
				.catch((error) => {
					if (error.message === 'Network Error') {
						this.snackbarStore.showError('application.network_error');
					} else if (error.response.status === 406) {
						this.snackbarStore.showError('profile.name_exists_error');
					} else {
						this.snackbarStore.showError('profile.update_name_error');
					}
				});
		}
	}

	public updateImage(event: ChangeEvent<HTMLInputElement>): void {
		const authToken: string = this.authStore.getAuthToken();

		if (!event.target.files) {
			return;
		}

		const image = event.target.files[0];

		this.profileService
			.updateImage(image, authToken)
			.then((imgUrl) => {
				this.authStore.setImgUrl(imgUrl);
				this.snackbarStore.showSuccess('profile.image_updated');
			})
			.catch(() => {
				this.snackbarStore.showError('profile.update_image_error');
			});
	}
}
